import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * Renders a Bank-Account selector dropdown with an inline "+ Add bank" option.
 * Props:
 *   value: bank_account_id | "" 
 *   onChange: (id) => void
 *   testId: optional data-testid
 */
export default function BankSelector({ value, onChange, testId = "bank-selector" }) {
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    bank_name: "", account_number: "", account_holder: "TULSI TRADING AND LOGISTICS",
    ifsc: "", branch: "", is_default: false,
  });

  const load = async () => setBanks((await api.get("/bank-accounts")).data);
  useEffect(() => { load(); }, []);

  // Auto-select default bank if no value yet
  useEffect(() => {
    if (!value && banks.length > 0) {
      const def = banks.find((b) => b.is_default) || banks[0];
      if (def) onChange(def.id);
    }
    // eslint-disable-next-line
  }, [banks]);

  const save = async () => {
    if (!form.bank_name || !form.account_number) return toast.error("Bank & account number required");
    try {
      const { data } = await api.post("/bank-accounts", form);
      toast.success("Bank added");
      setOpen(false);
      setForm({ bank_name: "", account_number: "", account_holder: "TULSI TRADING AND LOGISTICS", ifsc: "", branch: "", is_default: false });
      await load();
      onChange(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <Select value={value || "none"} onValueChange={(v) => {
        if (v === "__add__") { setOpen(true); return; }
        onChange(v === "none" ? "" : v);
      }}>
        <SelectTrigger data-testid={testId} className="mt-1.5 h-10 rounded-sm bg-white">
          <SelectValue placeholder="Select bank" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">— None / Cash —</SelectItem>
          {banks.map((b) => (
            <SelectItem key={b.id} value={b.id}>
              {b.bank_name} · {b.account_number.slice(-4).padStart(b.account_number.length, "•")}{b.is_default ? "  ★" : ""}
            </SelectItem>
          ))}
          <SelectItem value="__add__">+ Add new bank…</SelectItem>
        </SelectContent>
      </Select>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Add Bank Account</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Bank Name *</Label><Input className="mt-1.5 h-10 rounded-sm bg-white" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="inline-bank-name" /></div>
            <div><Label>Account Number *</Label><Input className="mt-1.5 h-10 rounded-sm bg-white font-mono" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} data-testid="inline-bank-account" /></div>
            <div><Label>IFSC</Label><Input className="mt-1.5 h-10 rounded-sm bg-white font-mono uppercase" value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} /></div>
            <div><Label>Branch</Label><Input className="mt-1.5 h-10 rounded-sm bg-white" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} data-testid="inline-save-bank-btn" className="rounded-sm bg-brand-forest text-white">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Bank
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
